import {useTheme, css} from "@emotion/react";
import PropTypes from "prop-types";
import {kebabCase} from "@/helpers/kebabCase";
import {useContext, useEffect, useRef, useCallback} from "react";
import {NavContext} from "@/core/providers/NavProvider";
import {ButtonPill} from "@/base";
import pdpBlades from "@/pages/products/detailBlades";
import BreadcrumbsNav from "@/core/navigation/Breadcrumbs";


const NavigationWidget = ({siblings}) => {

    const { stickyMenu } = useContext(NavContext);
    // eslint-disable-next-line no-unused-vars
    const [subNav, setSubNav] = stickyMenu;
    const initalNav = useRef();
    const isActive = useRef(false);
    const lastScroll = useRef(0);
    const scrollUp = useRef(false)

    const navItems = siblings.filter((link) => {
        let keep = false;
        if(link.render_name === 'quick_links_item') keep = true;
        if(link.render_name === 'placeholder'){
            let target = link.value.elements.find(el=>el.render_name==='target');
            if(target.value !== 'product_detail_nav') keep = true;
        }
        if(keep) return link;
    });

    /** Begin Sticky Logic **/

    useEffect(()=>{
        // remove from context when not in use
        return ()=>setSubNav(null)
    }, [setSubNav])

    const checkDirection = () => {
        let scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        scrollUp.current = scrollTop > lastScroll.current
        lastScroll.current = scrollTop <= 0 ? 0 : scrollTop; // For Mobile or negative scrolling
    };

    const scrollCheck = useCallback(() => {

        checkDirection()

        // since our sticky nav exists in the middle of the dom and not just beneath header already, we wait to move
        // it to the pageHeader via context till it is needed. Once there it can exist sticky along main nav sticky
        // with scroll to see useage

        let mainNavOffset = scrollUp.current ? 0 : document.getElementById('StickyMainNav').offsetHeight;

        // window scroll amount - the elements distance to the top (adding main nav height to catch prior to top)
        let activate = (window.scrollY - initalNav.current.offsetTop + mainNavOffset) > 0;

        if(isActive.current !== activate){
            setSubNav(activate ? navWidgetLayout(false) : null);
            isActive.current = activate;
        }

    }, [navWidgetLayout, setSubNav])

    useEffect(()=>{
        window.addEventListener('scroll', scrollCheck)
        return ()=>window.removeEventListener('scroll', scrollCheck)
    }, [scrollCheck])

    /** End sticky logic **/

    const theme = useTheme();

    const splashCss = css(`
        align-items: center;
        justify-content: center;
        display: flex;
        background: #000;
        width: 100%;
        padding: 1em;
        margin: 0 auto;
        border-bottom: 2px solid #e3e3e3;
    `);

    const styleMobileCatChooser = css(`
    padding: 1rem;
    width: 100%;
    select {
      outline:none;
      min-width: 100%;
      width: 100%;
      text-indent:.3em;
      padding: 1rem;
      background: #000;
      color: #fff;

      // Remove default select appearance for cross-browser consistency & force in a little down arrow thingy.
      -moz-appearance: none; 
      -webkit-appearance: none; 
      appearance: none;
      background-image: url('data:image/svg+xml;charset=utf-8;base64,PHN2ZyBhcmlhLWhpZGRlbj0idHJ1ZSIgZm9jdXNhYmxlPSJmYWxzZSIgZGF0YS1wcmVmaXg9ImZhciIgZGF0YS1pY29uPSJhbmdsZS1kb3duIiByb2xlPSJpbWciIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgdmlld0JveD0iMCAwIDMyMCA1MTIiIGNsYXNzPSJzdmctaW5saW5lLS1mYSBmYS1hbmdsZS1kb3duIGZhLXctMTAgZmEtM3giPjxwYXRoIGZpbGw9IiNmZmZmZmYiIGQ9Ik0xNTEuNSAzNDcuOEwzLjUgMjAxYy00LjctNC43LTQuNy0xMi4zIDAtMTdsMTkuOC0xOS44YzQuNy00LjcgMTIuMy00LjcgMTcgMEwxNjAgMjgyLjdsMTE5LjctMTE4LjVjNC43LTQuNyAxMi4zLTQuNyAxNyAwbDE5LjggMTkuOGM0LjcgNC43IDQuNyAxMi4zIDAgMTdsLTE0OCAxNDYuOGMtNC43IDQuNy0xMi4zIDQuNy0xNyAweiIgY2xhc3M9IiI+PC9wYXRoPjwvc3ZnPg==');
      background-repeat: no-repeat;
      background-position: calc(100% - 2rem) center;
      background-size: 1rem;
    }
    @media (min-width: ${theme.mobileNavBp}px) {
      display:none;
    }
  `);

  const flexItemCss = css(`
        display: none;
  
        @media (min-width: ${theme.mobileNavBp}px) {
            font-size: 14px;
            text-align:center;
            display: flex;
            align-items: center;
            justify-content: center; 
        }
`);


    const renderMobileCatNav = useCallback(() => (
        <div css={styleMobileCatChooser}>
          <select onChange={ev => handleChooseCat(ev)}>
              {navItems.map((link) => {
                  if(link.render_name === 'placeholder'){
                      let target = link.value.elements.find((attr)=>attr.render_name==='target');
                      let navItem = pdpBlades[target.value]?.props?.stepKey;
                      if(!navItem) return null
                      return <option key={navItem} value={navItem}>{navItem}</option>
                  } else {
                      let anchorItem = link.value.elements.find(el=>el.render_name==='quick_link_title');
                      const name = kebabCase(anchorItem.value);
                      return <option key={name} value={name}>{anchorItem.value}</option>
                  }

              })}
          </select>
        </div>
      ), [handleChooseCat, styleMobileCatChooser, navItems]);

     const labels = useCallback((initial) => {

         return navItems.map((link, linkIndex) => {

             const initAnimation = initial ? {
                         'data-aos' : "fade-in",
                         'data-aos-delay' : 500 + (200*linkIndex) ,
                         'data-aos-offset' : 50
                     } : null;

             if(link.render_name === 'placeholder'){
                 let target = link.value.elements.find((attr)=>attr.render_name==='target');

                 let stepItem = pdpBlades[target.value]?.props?.stepKey;
                 if(!stepItem) return null

                 const name = kebabCase(stepItem);
                 return (
                         <span {...initAnimation} key={stepItem}>
                            <ButtonPill to={`#${name}`} tagOverride={'a'} buttonTheme={'dark'} css={flexItemCss}>
                                {stepItem}
                            </ButtonPill>
                        </span>
                     )
             } else {
                 let anchorItem = link.value.elements.find(el=>el.render_name==='quick_link_title');
                 const name = kebabCase(anchorItem.value);
                 return (
                     <span {...initAnimation} key={anchorItem.id}>
                            <ButtonPill to={`#${name}`} tagOverride={'a'} buttonTheme={'dark'} css={flexItemCss}>
                                {anchorItem.value}
                            </ButtonPill>
                        </span>
                 )
             }
         })

     }, [flexItemCss, navItems]);


    const handleChooseCat = useCallback((ev) => {
        const sectionLabel = ev.target.value;
        if (ev.type === 'change') { // Handle select change
            document.location = `#${kebabCase(sectionLabel)}`
        }
        if (ev.type === 'keydown') { // Handle keyboard selection
        if (ev.code !== 'Space' && ev.code !== 'Enter') {
            document.location = `#${kebabCase(sectionLabel)}`
        }
        ev.preventDefault();
        }
    }, []);

    const navWidgetLayout = useCallback((initial) => {
        return (
            <div>
                <div css={splashCss} ref={initial ? initalNav : null}>
                {labels(initial)}
                {renderMobileCatNav()}    
                </div>
                <BreadcrumbsNav />
            </div>

        )
    }, [labels, renderMobileCatNav, splashCss]);

    return navWidgetLayout(true);
}

NavigationWidget.propTypes = {
    product: PropTypes.object,
};

export default NavigationWidget;
